import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Sprout, Users, Building2, Star, Quote, Award, Compass } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { koperasiInfo, heroImages, pengurus, testimonials, galeri, formatNumber } from "@/lib/dummyData";
import { PublicAPI } from "@/lib/api";
import { useEffect, useState } from "react";

const milestones = [
  { year: "2008", text: "Koperasi Andalan Dapur Nusantara didirikan dengan 27 anggota awal di Yogyakarta." },
  { year: "2013", text: "Memperoleh sertifikasi koperasi sehat dari Dinas Koperasi DIY." },
  { year: "2017", text: "Membuka layanan toko ritel pertama dengan kemitraan UMKM lokal." },
  { year: "2021", text: "Transformasi digital — platform anggota online resmi diluncurkan." },
  { year: "2026", text: "4.800+ anggota, 6 cabang, dan ekspansi ke Magelang." },
];

const keunggulan = [
  { icon: Sprout, title: "SHU Transparan", desc: "Hitungan SHU otomatis dari jasa modal & jasa usaha, dapat dilihat real-time." },
  { icon: Award, title: "Harga Khusus Anggota", desc: "Selisih harga 8–15% di toko koperasi, plus akses flash sale anggota." },
  { icon: Users, title: "Pinjaman Lunak", desc: "Bunga 0,8% per bulan, plafon hingga Rp 50 juta tanpa agunan untuk anggota aktif." },
  { icon: Compass, title: "Pendampingan UMKM", desc: "Kelas literasi keuangan, mentor UMKM, dan jaringan supplier lokal." },
];

export default function Profile() {
  // Statistik ringkas koperasi (nyata dari backend).
  const [stats, setStats] = useState(null);
  useEffect(() => { PublicAPI.stats().then(setStats).catch(() => {}); }, []);

  return (
    <div>
      <section className="mx-auto max-w-7xl px-5 lg:px-10 pt-10 lg:pt-16 pb-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="pill-tag bg-forest/10 text-forest mb-5">Profil Koperasi</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-[1.05] tracking-tight">
              Sejak <span className="font-editorial italic">{koperasiInfo.founded}</span>,
              kami menumbuhkan ekonomi rakyat satu desa pada satu waktu.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-ink/75 leading-relaxed text-base">
              Koperasi Andalan Dapur Nusantara lahir dari kesadaran sederhana: ekonomi terbaik adalah
              yang menempatkan manusia, bukan modal, sebagai pusatnya. Selama 18 tahun
              kami terus menumbuhkan jaringan gotong-royong yang adil, modern, dan terukur.
            </p>
            {/* Statistik langsung dari sistem */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                { label: "Anggota", value: formatNumber(stats ? stats.members : koperasiInfo.members) },
                { label: "Gerai Unit", value: formatNumber(stats ? stats.outlets : koperasiInfo.villages) },
                { label: "Produk", value: formatNumber(stats ? stats.products : 8) },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-stone-soft bg-white p-4 text-center">
                  <div className="font-display font-bold text-2xl text-forest">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-wider text-ink/55 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 rounded-3xl overflow-hidden aspect-[16/10] border border-stone-soft">
            <img src={heroImages.team} alt="Tim koperasi" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-5 grid grid-rows-2 gap-5">
            <Card className="rounded-3xl border-stone-soft p-7 bg-forest text-cream">
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-warm font-semibold">Visi</div>
              <p className="font-display text-xl lg:text-2xl font-semibold mt-3 leading-snug">
                Menjadi koperasi rakyat paling tepercaya di Indonesia melalui transparansi dan teknologi.
              </p>
            </Card>
            <Card className="rounded-3xl border-stone-soft p-7 bg-amber-warm text-ink">
              <div className="text-[10px] uppercase tracking-[0.22em] text-ink/70 font-semibold">Misi</div>
              <p className="font-display text-base lg:text-lg font-medium mt-3 leading-relaxed">
                Memberdayakan UMKM, memperluas akses keuangan inklusif, dan membagi
                kesejahteraan secara adil melalui platform digital yang ramah semua usia.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Keunggulan */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
        <div className="mb-12">
          <div className="pill-tag bg-sage/20 text-forest mb-4">Keunggulan Keanggotaan</div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight">Mengapa anggota memilih kami</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {keunggulan.map((k) => (
            <Card key={k.title} className="rounded-2xl border-stone-soft p-6 hover-lift">
              <div className="w-12 h-12 rounded-xl bg-cream grid place-items-center mb-4 text-forest">
                <k.icon size={22} />
              </div>
              <h3 className="font-display font-semibold text-ink text-lg">{k.title}</h3>
              <p className="text-sm text-ink/75 mt-2 leading-relaxed">{k.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Timeline / Sejarah */}
      <section className="bg-white border-y border-stone-soft py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="mb-12">
            <div className="pill-tag bg-terracotta/10 text-terracotta mb-4">Perjalanan Kami</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight max-w-2xl">Tumbuh perlahan, namun pasti.</h2>
          </div>
          <div className="relative grid lg:grid-cols-5 gap-6">
            {milestones.map((m, i) => (
              <div key={m.year} className={`relative rounded-2xl p-5 border ${i % 2 === 0 ? "border-forest/30 bg-forest text-cream" : "border-stone-soft bg-cream text-ink"}`}>
                <div className={`font-editorial italic text-4xl font-medium ${i % 2 === 0 ? "text-amber-warm" : "text-forest"}`}>{m.year}</div>
                <p className={`text-sm mt-3 leading-relaxed ${i % 2 === 0 ? "text-cream/90" : "text-ink/75"}`}>{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Struktur */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
          <div>
            <div className="pill-tag bg-amber-warm/15 text-amber-700 mb-4">Pengurus & Pengawas</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight">Wajah di balik koperasi</h2>
          </div>
          <p className="text-ink/75 max-w-md text-sm">Dipilih langsung oleh anggota dalam Rapat Anggota Tahunan setiap 3 tahun sekali.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {pengurus.map((p) => (
            <div key={p.name} className="group">
              <div className="aspect-square rounded-2xl overflow-hidden bg-cream border border-stone-soft mb-3">
                <img src={p.avatar} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <div className="font-display font-semibold text-sm text-ink leading-tight">{p.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-ink/55 mt-1">{p.role}</div>
              <div className="text-[11px] text-forest mt-0.5">Sejak {p.since}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Galeri */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 pb-20">
        <div className="mb-8">
          <div className="pill-tag bg-forest/10 text-forest mb-4">Galeri Kegiatan</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Momen kebersamaan</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5">
          {galeri.map((g, i) => (
            <div key={i} className={`rounded-2xl overflow-hidden border border-stone-soft ${i === 0 ? "lg:row-span-2 aspect-square lg:aspect-auto" : "aspect-[4/3]"}`}>
              <img src={g} alt={`Galeri ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </section>

      {/* Testimoni grid */}
      <section className="bg-cream border-y border-stone-soft py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="pill-tag bg-sage/20 text-forest mb-4">Suara Anggota</div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight max-w-2xl mb-12">
            Apa kata mereka yang sudah lebih dulu bergabung
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {testimonials.map((t) => (
              <Card key={t.name} className="rounded-2xl border-stone-soft p-6 bg-white hover-lift">
                <Quote className="text-amber-warm" size={24} />
                <p className="text-sm text-ink mt-3 leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 pt-4 border-t border-stone-soft/60">
                  <div className="font-display font-semibold text-sm text-ink">{t.name}</div>
                  <div className="flex text-amber-warm gap-0.5 mt-1">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Kontak */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="pill-tag bg-terracotta/10 text-terracotta mb-4">Hubungi Kami</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight">Datang berkunjung, kami selalu siap.</h2>
            <p className="text-ink/75 mt-4 leading-relaxed">Kantor pusat buka Senin–Sabtu, 08.00–17.00 WIB. Layanan online 24/7 lewat aplikasi.</p>

            <div className="mt-8 space-y-4">
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 rounded-xl bg-forest text-white grid place-items-center shrink-0"><MapPin size={20} /></div>
                <div>
                  <div className="font-semibold text-ink">Kantor Pusat</div>
                  <div className="text-sm text-ink/75">{koperasiInfo.address}</div>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 rounded-xl bg-amber-warm text-ink grid place-items-center shrink-0"><Phone size={20} /></div>
                <div>
                  <div className="font-semibold text-ink">Telepon</div>
                  <div className="text-sm text-ink/75">{koperasiInfo.phone}</div>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 rounded-xl bg-sage text-ink grid place-items-center shrink-0"><Mail size={20} /></div>
                <div>
                  <div className="font-semibold text-ink">Email</div>
                  <div className="text-sm text-ink/75">{koperasiInfo.email}</div>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <Link to="/daftar"><Button className="rounded-full bg-forest hover:bg-forest-dark text-white h-12 px-7" data-testid="profile-cta-daftar">Daftar Anggota Online</Button></Link>
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden border border-stone-soft aspect-[4/3] lg:aspect-auto bg-cream relative">
            <iframe
              title="map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=110.36%2C-7.79%2C110.39%2C-7.77&amp;layer=mapnik"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
